/**
 * What the open-account dialog offers (DEV-162): the currencies offered
 * (Settings → Organisation → Currencies offered) that at least one switched-on
 * provider can hold, in the offered order, and for every other offered
 * currency the reason it is missing. A provider's list is what it can open
 * today, already intersected with the offered list by the API; empty means
 * none, never "any".
 */

export interface AccountProviderOption {
  readonly id: string;
  readonly label: string;
  readonly enabled: boolean;
  readonly currencies: readonly string[];
  readonly unavailableCurrencies?: readonly { readonly currency: string; readonly reason: string }[];
  /** Said once about the whole list, e.g. that IBANQ was not asked. */
  readonly currencyNote?: string;
}

export interface CurrencyChoice {
  readonly code: string;
  /** How many switched-on providers can open it. */
  readonly providers: number;
}

export interface MissingCurrency {
  readonly code: string;
  readonly why: string;
}

export interface OpenAccountCurrencies {
  readonly choosable: readonly CurrencyChoice[];
  readonly missing: readonly MissingCurrency[];
}

/** Providers that can open an account in `currency` now. */
export function providersFor<P extends AccountProviderOption>(providers: readonly P[], currency: string): P[] {
  return providers.filter((p) => p.enabled && p.currencies.includes(currency));
}

export function openAccountCurrencies(offered: readonly string[], providers: readonly AccountProviderOption[]): OpenAccountCurrencies {
  const enabled = providers.filter((p) => p.enabled);
  const choosable: CurrencyChoice[] = [];
  const missing: MissingCurrency[] = [];
  for (const code of offered) {
    const count = providersFor(providers, code).length;
    if (count > 0) {
      choosable.push({ code, providers: count });
      continue;
    }
    const reasons = enabled
      .map((p) => p.unavailableCurrencies?.find((u) => u.currency === code)?.reason)
      .filter((reason): reason is string => reason !== undefined);
    missing.push({
      code,
      why: enabled.length === 0
        ? 'No provider is switched on.'
        : reasons.length > 0 ? reasons.join(' ') : 'No switched-on provider can hold it.',
    });
  }
  return { choosable, missing };
}
